import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import type MarketSettingsCurrencyConversionProfileController from 'lumen/controllers/market/settings/currency-conversion/profile';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
} from 'lumen/models';
import type { Transition } from 'lumen/services/transition';
import { isErrorWithStatusCode } from 'lumen/utils/budget-forecast/data-fetching';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type CurrencyConversionProfileModel = {
  baseCurrencies: TaskForAsyncTaskFunction<
    { restartable: boolean },
    () => Promise<Currency[]>
  >;
  exchangeRatesProfile: TaskForAsyncTaskFunction<
    { restartable: boolean },
    (id: string) => Promise<CurrencyConversionExchangeRatesProfile>
  >;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: ['settings.exchange_rate_profiles.view'],
  redirectTo: 'market.settings',
})
export default class MarketSettingsCurrencyConversionProfileRoute extends Route {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @tracked transitionHandler: ((transition: PublicTransition) => void) | null =
    null;

  model(params: { profile_id: string }): CurrencyConversionProfileModel {
    const model: CurrencyConversionProfileModel = {
      exchangeRatesProfile:
        this.currencyConversion.getExchangeRatesProfileByIdTask,
      baseCurrencies: this.currencyConversion.getBaseCurrenciesTask,
    };

    void this.performExchangeRatesProfile(params.profile_id);
    return model;
  }

  async performExchangeRatesProfile(profileId: string): Promise<void> {
    try {
      await this.currencyConversion.getExchangeRatesProfileByIdTask.perform(
        profileId,
      );
    } catch (error: unknown) {
      if (isErrorWithStatusCode(error) && error.status === 404) {
        this.router.transitionTo('market.settings.currency-conversion.index', {
          queryParams: { profileNotFound: true },
        });
        return;
      }
      // getExchangeRatesProfileByIdTask already captured + wrapped this error;
      // propagate it untouched instead of re-capturing (single Sentry event).
      if (error instanceof SilentTaskError) {
        throw error;
      }
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  }

  resetController(
    controller: MarketSettingsCurrencyConversionProfileController,
    isExiting: boolean,
    transition: PublicTransition,
  ): void {
    if (this.transitionHandler) {
      this.router.off('routeWillChange', this.transitionHandler);
      this.transitionHandler = null;
    }

    super.resetController(controller, isExiting, transition);

    if (isExiting) {
      controller.resetDiscardedChanges();
    }
  }

  setupController(
    controller: MarketSettingsCurrencyConversionProfileController,
    model: ModelFrom<MarketSettingsCurrencyConversionProfileRoute>,
    transition: PublicTransition,
  ): void {
    super.setupController(controller, model, transition);

    this.transitionHandler = (t: PublicTransition): void => {
      void this.currencyConversion.onRouteWillChange(
        this.routeName,
        controller,
        t as Transition,
      );
    };
    this.router.on('routeWillChange', this.transitionHandler);
  }
}
